/**
 * Why a builder request did not produce a usable answer.
 * - `offline`: the request never reached StackJe (network failure).
 * - `unavailable`: StackJe answered with a server error or an unreadable body.
 * - `signed-out`: the session is missing or expired (403).
 * - `not-found`: the site does not exist or belongs to someone else (404).
 * - `rejected`: the server refused the request as sent, e.g. a stale review (400).
 */
export type BuilderFailure = "offline" | "unavailable" | "signed-out" | "not-found" | "rejected";

export class BuilderRequestError extends Error {
  readonly failure: BuilderFailure;
  readonly status: number | null;

  constructor(failure: BuilderFailure, status: number | null) {
    super(`Builder request failed: ${failure}${status === null ? "" : ` (${status})`}`);
    this.name = "BuilderRequestError";
    this.failure = failure;
    this.status = status;
  }

  /** A mutation with this failure may or may not have been applied; resend it with the same action ID. */
  get outcomeUnknown(): boolean {
    return this.failure === "offline" || this.failure === "unavailable";
  }
}

export function failureForStatus(status: number): BuilderFailure {
  if (status === 403 || status === 401) return "signed-out";
  if (status === 404) return "not-found";
  if (status >= 400 && status < 500) return "rejected";
  return "unavailable";
}

export function toBuilderError(caught: unknown): BuilderRequestError {
  if (caught instanceof BuilderRequestError) return caught;
  return new BuilderRequestError("offline", null);
}

/** The blog refused a change and said why, in words for the owner (an address already taken, a post that isn't ready). */
export class BlogRefusedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BlogRefusedError";
  }
}

/** An owner-readable catalogue refusal, distinct from network failures or concurrency conflicts. */
export class ShopRefusedError extends Error {
  constructor(message: string) { super(message); this.name = "ShopRefusedError"; }
}
